/**
 * What this learner is wearing, for the whole tab.
 *
 * The figure itself is lib/avatar.ts — the parts, the rails and the drawing.
 * This is the other half: which seed is current, where it is kept, who has to
 * be told when it changes, and what happens to it when somebody signs in.
 *
 * ## A module, not a hook
 *
 * The same answer lib/theme.ts gives. Three unrelated places need this value —
 * the profile screen that builds it, the chat sockets that put it on the wire,
 * the corner of the level list that draws it — and they are in three different
 * branches of the tree with a router between them. A context would have to
 * wrap the whole app to serve components that are mounted one at a time, and a
 * prop would have to be threaded through every screen. So it is a value with a
 * subscribe and a read, and `useSyncExternalStore` is exactly what that is.
 *
 * ## The ladder
 *
 * What a room shows beside a name is decided in this order, and the first one
 * that exists wins:
 *
 *   1. the operator this learner built            `worn()`
 *   2. the emoji face they picked                 ChatPrefs.face
 *   3. the picture on their Google account        AccountUser.picture
 *   4. the operator their id gives them           `derived()`
 *
 * The third rung is the one this feature had to be careful with. A signed-in
 * person has been shown as their account picture since long before there were
 * figures, and arriving one morning as a stranger made of pixels would be a
 * new feature quietly taking something away — so a picture stays until the
 * person themselves builds something, and then the figure is on top of it
 * everywhere. That is the whole of what the builder promises.
 *
 * The fourth is why nobody is ever faceless and why nobody is ever *asked*: a
 * learner who never opens the builder still arrives in a room as a particular
 * person, the same particular person on every device once they have signed in,
 * and the builder is then a change of mind rather than a form to fill in. It
 * is derived from the parts that are open to everybody, so it holds still as
 * they clear levels (lib/avatar.ts, `selectionFromId`).
 *
 * The room has a fifth rung underneath all four — the emoji face it derives
 * from the tag — and that one is for a tab that tells it nothing, which is to
 * say a build older than this file (worker/room.ts).
 *
 * ## Two places to keep it, and one rule
 *
 * Signed out, the seed is in this browser beside the chat identity, and it is
 * mirrored into the vault with it (lib/chat.ts). Signed in, it is also a
 * column on the account, which is what makes it the same figure on a phone and
 * a laptop.
 *
 * `syncOperator` reconciles the two once per load, and the rule is not a merge
 * — two figures are not a figure, and the max of a haircut is not a haircut.
 * The account wins where it has an answer, because it is the copy that means
 * "this is what I chose", and this browser's copy pushes up only when the
 * account has none. So building a figure signed out and then signing in keeps
 * it, and signing in on a borrowed browser does not overwrite what you built
 * on your own.
 */
import { chatPrefs, updatePrefs } from './chat';
import { pushAvatar } from './account';
import { parseSeed, readSeed, selectionFromId, toSeed, type Selection } from './avatar';

/** Notified when the worn seed changes: the sockets, and anything drawing it. */
const listeners = new Set<() => void>();

/**
 * The current value, cached so that `useSyncExternalStore` gets a stable
 * string between changes. `chatPrefs()` is itself cached, but it hands back a
 * fresh object on the load that reads storage and this has to answer from the
 * first render onwards without touching it twice.
 */
let current: string | null | undefined;

/** The operator this learner built, or null while they wear a face or the one their id gives them. */
export function worn(): string | null {
  if (current === undefined) current = chatPrefs().seed;
  return current;
}

export function onOperator(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function announce(seed: string | null): void {
  current = seed;
  for (const fn of [...listeners]) fn();
}

/**
 * The figure an id gives, as a seed.
 *
 * The account id where there is one, so that the same person is the same
 * figure everywhere, and this browser's chat id where there is not. Never the
 * tag: the tag is a hash and is four characters wide, which is fifteen bits to
 * choose nine parts with, and a room of sixteen people would have two of them
 * wearing the same face.
 */
export function derived(id: string): string {
  return toSeed(selectionFromId(id));
}

/**
 * What this browser tells a room it is wearing.
 *
 * Null is a real answer and means "you decide": it is what a tab says when the
 * learner has built no figure, picked no face, and has a picture on their
 * Google account — because that picture is what the room has been showing them
 * as since before any of this existed, and replacing it with a figure nobody
 * asked for would be this feature taking something away. Building one puts it
 * back on top, which is the whole of what the builder promises.
 *
 * Everybody else is told explicitly, derived figure included. The room's own
 * fallback is an emoji face from the tag, and it is there for a tab running a
 * build older than this file rather than for anyone who could send this.
 */
export function wearingOnWire(id: string | null, face: string | null, picture: string | null): string | null {
  const mine = worn() ?? face;
  if (mine) return mine;
  if (picture) return null;
  return id ? derived(id) : null;
}

/**
 * What the tab should draw for itself, outside a room: the corner of the level
 * list, the profile screen. The same ladder as the wire, with the account
 * picture in it rather than left to the room, and never null.
 */
export function wearingHere(id: string, face: string | null, picture: string | null): string {
  return worn() ?? face ?? picture ?? derived(id);
}

/** The worn seed as a selection the builder and the renderers can use. */
export function wornSelection(id: string | null): Selection {
  return parseSeed(worn() ?? (id ? derived(id) : ''));
}

/**
 * Wear this figure — or null, to go back to the one your id gives you.
 *
 * Written to this browser first and announced immediately, because the
 * builder's Save is a direct manipulation of something on screen and must not
 * wait on a round trip. The account is told afterwards; `false` comes back
 * when that did not land, and the caller says so without taking the figure
 * away. The next sign-in reconciles it, and a browser that is offline is
 * exactly the browser that should keep what it was given.
 */
export async function wear(seed: string | null, signedIn: boolean): Promise<boolean> {
  const clean = seed === null ? null : readSeed(seed);
  if (seed !== null && clean === null) return false;
  updatePrefs({ seed: clean });
  announce(clean);
  if (!signedIn) return true;
  return pushAvatar(clean);
}

/**
 * Put an emoji face on instead.
 *
 * Not a third state to keep track of: wearing a face *is* not wearing an
 * operator, so the seed goes — here and on the account, or the next load would
 * pull the figure back down over the face they just chose. The face itself is
 * the chat's own preference and is written by its caller (state/useChat.ts).
 */
export async function wearFace(signedIn: boolean): Promise<boolean> {
  if (worn() === null) return true;
  return wear(null, signedIn);
}

/**
 * Reconcile this browser's figure with the account's, once per load and again
 * whenever the signed-in user changes.
 *
 * See the header for the rule. Returns what the learner is wearing afterwards,
 * which the caller has no use for and a test does.
 */
export async function syncOperator(accountSeed: string | null, signedIn: boolean): Promise<string | null> {
  const mine = worn();
  if (!signedIn) return mine;
  const theirs = accountSeed === null ? null : readSeed(accountSeed);
  if (theirs !== null) {
    if (theirs !== mine) {
      updatePrefs({ seed: theirs });
      announce(theirs);
    }
    return theirs;
  }
  // The account has no figure and this browser does: the one they built before
  // they signed in, or on the device they built it on. Push it up rather than
  // drop it — a learner who signs in to keep their record should not find that
  // it cost them their face.
  if (mine !== null) void pushAvatar(mine);
  return mine;
}

/** Test seam: forget the cached value so a second read goes back to storage. */
export function resetOperatorCache(): void {
  current = undefined;
}
